import * as THREE from 'three/webgpu';
import type {EntityPatch} from '../../history/types';
import {SlotLengthType} from '../../draw/SketchSlotDrawingHandler';
import type {SketchSlotObject} from '../../mesh/sketch/SketchSlotObject';
import type {SketchPointObject} from '../../mesh/sketch/SketchPointObject';
import type {SketchDimensionIdentityService} from '../dimension/repository/SketchDimensionIdentityService';
import type {SketchDimensionRepository} from '../dimension/repository/SketchDimensionRepository';
import type {SketchDimensionReferenceRecord} from '../dimension/SketchDimensionTypes';
import {
    createPlannedSketchDimensions,
    type PlannedSketchDimension,
} from '../dimension/createPlannedSketchDimensions';

/** 标注沿"离开轮廓"方向的外移量相对槽口半宽的倍数（长度/宽度/角度标注共用）。 */
const LABEL_OFFSET_HALF_WIDTH_FACTOR = 2.2;

/** 半径标注落在圆心到中心弧的这个比例处（SW 的 R 引线在弧内侧）。 */
const RADIUS_LABEL_RADIUS_RATIO = 0.55;

/** 几何退化阈值（米）。 */
const DEGENERATE_LENGTH = 1e-9;

/** 自动尺寸创建输入。 */
export interface SlotAutoDimensionInput {
    /** 尺寸仓库。 */
    readonly repository: SketchDimensionRepository;
    /** 草图持久 ID。 */
    readonly sketchId: string;
    /** 槽口运行时对象。 */
    readonly slot: SketchSlotObject;
    /** 直槽口长度语义（中心到中心 / 总长）。 */
    readonly lengthType: SlotLengthType;
    /** 身份服务（缺省新建）。 */
    readonly identityService?: SketchDimensionIdentityService;
}


/** 读取点的草图局部二维坐标。 */
function readLocal(point: SketchPointObject): THREE.Vector2 {
    const local = point.getLocalPosition2D();

    return new THREE.Vector2(local.x, local.y);
}

/**
 * 构造点引用记录。
 * @param point 被引用点。
 * @param selectionOrder 选择顺序。
 * @param hostEntityId 点所属实体持久 ID；独立点缺省使用自身持久 ID。
 * @returns 点尺寸引用记录。
 */
function pointReference(
    point: SketchPointObject,
    selectionOrder: number,
    hostEntityId: string = point.persistentId,
): SketchDimensionReferenceRecord {
    return {
        entityId: hostEntityId,
        pointId: point.persistentId,
        topologyRole: 'point',
        selectionOrder,
    };
}

/**
 * 构造端帽圆弧圆心引用。
 * @param arcPersistentId 端帽圆弧持久 ID。
 * @param centerPoint 端帽圆弧圆心点。
 * @param selectionOrder 选择顺序。
 * @returns 以端帽圆弧为宿主的圆心引用。
 */
function capCenterReference(
    arcPersistentId: string,
    centerPoint: SketchPointObject,
    selectionOrder: number,
): SketchDimensionReferenceRecord {
    return {
        entityId: arcPersistentId,
        pointId: centerPoint.persistentId,
        topologyRole: 'center',
        selectionOrder,
    };
}

/**
 * 构造端帽弧「远侧圆周」引用（总长语义：测量值 = 圆心距 + 两端半径）。
 *
 * @param arcPersistentId 端帽弧持久 ID。
 * @param centerPoint 端帽弧圆心点。
 * @param selectionOrder 选择顺序。
 * @returns 引用记录。
 */
function capRimReference(
    arcPersistentId: string,
    centerPoint: SketchPointObject,
    selectionOrder: number,
): SketchDimensionReferenceRecord {
    return {
        ...capCenterReference(arcPersistentId, centerPoint, selectionOrder),
        arcCondition: 'max',
    };
}

/** 规划直槽口自动尺寸（长度 + 宽度）。 */
function planStraightSlotDimensions(
    slot: SketchSlotObject,
    lengthType: SlotLengthType,
): PlannedSketchDimension[] {
    const startPoint = slot.centerlineStartPoint;
    const endPoint = slot.centerlineEndPoint;
    const junction = slot.junctionPoints;

    if (!startPoint || !endPoint) {
        return [];
    }

    const start = readLocal(startPoint);
    const end = readLocal(endPoint);
    const axis = end.clone().sub(start);
    const centerDistance = axis.length();
    const halfWidth = slot.startCapArc.getArcDefinition().radius;

    if (centerDistance < DEGENERATE_LENGTH || halfWidth < DEGENERATE_LENGTH) {
        return [];
    }

    const axisDirection = axis.clone().normalize();
    const normal = new THREE.Vector2(-axisDirection.y, axisDirection.x);
    const labelOffset = halfWidth * LABEL_OFFSET_HALF_WIDTH_FACTOR;
    const axisMid = start.clone().add(end).multiplyScalar(0.5);
    /** 长度标注落在中心线上方（沿法向偏移一个槽口宽度量级）。 */
    const lengthLabel = axisMid.clone().add(normal.clone().multiplyScalar(labelOffset));
    const p1 = readLocal(junction.p1);
    const p4 = readLocal(junction.p4);
    /** 宽度标注落在起始端帽外侧（沿轴反向偏移）。 */
    const widthLabel = p1.clone().add(p4).multiplyScalar(0.5)
        .add(axisDirection.clone().multiplyScalar(-labelOffset));
    const isOverall = lengthType === SlotLengthType.Overall;
    /** 总长 = 圆心距 + 两端半径（对齐 SW 的 Overall Length）。 */
    const lengthValue = isOverall ? centerDistance + halfWidth * 2 : centerDistance;
    const lengthReferences = isOverall
        ? [
            capRimReference(slot.startCapArc.persistentId, startPoint, 0),
            capRimReference(slot.endCapArc.persistentId, endPoint, 1),
        ]
        : [
            capCenterReference(slot.startCapArc.persistentId, startPoint, 0),
            capCenterReference(slot.endCapArc.persistentId, endPoint, 1),
        ];

    return [
        {
            dimensionKind: 'point-distance',
            nominalValue: lengthValue,
            references: lengthReferences,
            labelPosition: [lengthLabel.x, lengthLabel.y],
        },
        {
            dimensionKind: 'point-distance',
            nominalValue: halfWidth * 2,
            references: [
                pointReference(junction.p1, 0, slot.startCapArc.persistentId),
                pointReference(junction.p4, 1, slot.startCapArc.persistentId),
            ],
            labelPosition: [widthLabel.x, widthLabel.y],
        },
    ];
}

/** 规划圆弧槽口自动尺寸（半径 + 角度 + 宽度）。 */
function planArcSlotDimensions(slot: SketchSlotObject): PlannedSketchDimension[] {
    const centerPoint = slot.arcCenterPoint;
    const midpointPoint = slot.centerArcMidpoint;
    const outerArc = slot.outerArc;
    const junction = slot.junctionPoints;

    if (!centerPoint || !midpointPoint || !outerArc) {
        return [];
    }

    const center = readLocal(centerPoint);
    const midpoint = readLocal(midpointPoint);
    const radius = midpoint.clone().sub(center).length();
    const halfWidth = slot.startCapArc.getArcDefinition().radius;

    if (radius < DEGENERATE_LENGTH || halfWidth < DEGENERATE_LENGTH) {
        return [];
    }

    const startCapCenter = slot.startCapArc.getCenterPoint();
    const endCapCenter = slot.endCapArc.getCenterPoint();
    const startDirection = readLocal(startCapCenter).sub(center);
    const endDirection = readLocal(endCapCenter).sub(center);
    /**
     * 扫角取**槽口自身的有符号跨角**，与求解快照同一套推导。
     *
     * 不能用两射线夹角的绝对值：那个量恒 ≤180°，槽口跨过平角时得到的是另一侧的
     * 补角——标注数字与槽口实际跨过的弧对不上（SW 中同一形状显示的是优角）。
     */
    const clockwise = outerArc.getArcDefinition().clockwise;
    const startAngle = Math.atan2(startDirection.y, startDirection.x);
    const rawEndAngle = Math.atan2(endDirection.y, endDirection.x);
    let sweep = clockwise ? startAngle - rawEndAngle : rawEndAngle - startAngle;

    sweep %= Math.PI * 2;
    if (sweep <= 0) {
        sweep += Math.PI * 2;
    }

    /** 起端帽 → 止端帽的旋转方向：逆时针为 +1，供三点角方程延续同一旋向。 */
    const directionSign: 1 | -1 = clockwise ? -1 : 1;
    const outward = midpoint.clone().sub(center).normalize();
    const startRadial = startDirection.clone().normalize();
    /**
     * 起端帽处**背离槽口**的切向：槽体沿 directionSign 方向展开，
     * 反向切线即槽口之外，宽度标注落在那里（对齐 SW 的默认放置）。
     */
    const startTangentAway = new THREE.Vector2(startRadial.y, -startRadial.x)
        .multiplyScalar(directionSign);
    const p1 = readLocal(junction.p1);
    const p4 = readLocal(junction.p4);
    const widthLabel = p1.clone().add(p4).multiplyScalar(0.5)
        .add(startTangentAway.multiplyScalar(halfWidth * LABEL_OFFSET_HALF_WIDTH_FACTOR));
    /** 半径标注落在圆心与中心弧之间（SW 的 R 引线同侧）。 */
    const radiusLabel = center.clone().add(outward.clone().multiplyScalar(radius * RADIUS_LABEL_RADIUS_RATIO));
    const planned: PlannedSketchDimension[] = [
        {
            /**
             * 中心弧半径：真半径尺寸，引用槽口实体本身（槽口的中心弧就是它的半径参数）。
             * 曾经建成「圆心到中心弧中点」的两点距离——数字前缀是 R、语义却是距离，
             * 于是圆心点上多出一条`距离`关系，且拖动时表现为两点距离而非半径。
             */
            dimensionKind: 'radius',
            nominalValue: radius,
            references: [{
                entityId: slot.persistentId,
                topologyRole: 'entity',
                selectionOrder: 0,
            }],
            labelPosition: [radiusLabel.x, radiusLabel.y],
        },
        {
            dimensionKind: 'point-distance',
            nominalValue: halfWidth * 2,
            references: [
                pointReference(junction.p1, 0, slot.startCapArc.persistentId),
                pointReference(junction.p4, 1, slot.startCapArc.persistentId),
            ],
            labelPosition: [widthLabel.x, widthLabel.y],
        },
    ];

    /**
     * 角度尺寸恒按「中心到中心」测量（SW 明确规定），即圆心为顶点、
     * 两端帽弧圆心为射线端点的三点角；跨角为优角时同样标注。
     */
    if (sweep > DEGENERATE_LENGTH && Math.PI * 2 - sweep > DEGENERATE_LENGTH) {
        /** 角度标注落在外弧之外（与半径标注分居内外，互不压字）。 */
        const angleLabel = center.clone()
            .add(outward.clone().multiplyScalar(radius + halfWidth * LABEL_OFFSET_HALF_WIDTH_FACTOR));

        planned.push({
            dimensionKind: 'three-point-angle',
            nominalValue: sweep,
            references: [
                pointReference(centerPoint, 0),
                pointReference(startCapCenter, 1),
                pointReference(endCapCenter, 2),
            ],
            labelPosition: [angleLabel.x, angleLabel.y],
            branch: {directionSign},
        });
    }

    return planned;
}

/**
 * 槽口创建时自动添加尺寸（对齐 SOLIDWORKS「添加尺寸」选项）。
 *
 * 语义（官方 Slot PropertyManager）：
 * 1. 直槽口标注长度与宽度，长度按 `lengthType` 取中心到中心或总长；
 * 2. 圆弧槽口标注中心弧半径、角度与宽度，角度恒按中心到中心测量；
 * 3. 生成的是**真实尺寸**（进仓库、可驱动、可持久化、可撤销），不是显示层装饰。
 *
 * 事务纪律：本函数只写仓库并返回补丁，由调用方与槽口创建的运行时快照
 * 合成**同一笔** CadTransaction（HIST-001），保证撤销一步回到无槽口无尺寸。
 *
 * @param input 创建输入。
 * @returns 尺寸历史补丁列表；无可标注项时为空数组。
 */
export function createSlotAutoDimensions(input: SlotAutoDimensionInput): EntityPatch[] {
    const planned = input.slot.isArcSlot()
        ? planArcSlotDimensions(input.slot)
        : planStraightSlotDimensions(input.slot, input.lengthType);


    /**
     * 自动尺寸一律 driving（对齐 SW）：直槽口与圆弧槽口都已参数化，
     * 改值能精确驱动整槽；落库走全仓唯一一份 `createPlannedSketchDimensions`。
     */
    return createPlannedSketchDimensions({
        repository: input.repository,
        sketchId: input.sketchId,
        planned,
        ...(input.identityService ? {identityService: input.identityService} : {}),
    });
}
